import moment from 'moment';
import type { StacCollection, StacProvider } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import { isCreditedProvider, isOurProvider, NATUREMETRICS_PROVIDER } from 'src/shared/catalogue/catalogueAttribution';
import { utcDayOf } from './asOfInstant';

/**
 * Turning the dataset form into the document the engine validates, and back.
 *
 * The engine checks incoming collections against the published STAC JSON Schema
 * (ADR-016 decision 9), so the shape here is the schema's, not ours. What this
 * module adds is the part a schema cannot: catching the mistakes worth catching
 * before a round trip, and carrying forward every field the form has no input
 * for — a collection is an open document, and an edit must not silently strip
 * `keywords`, `summaries` or an extension a producer relies on.
 */
export type SpatialExtentChoice = 'global' | 'regional';
export type TemporalEndChoice = 'ongoing' | 'ended';

export type CollectionFormValues = {
    id: string;
    title: string;
    description: string;
    license: string;
    /**
     * Who must be credited when this dataset is used, as a provider.
     *
     * STAC's own home for credit: the `licensor` role says who licenses the data, `description`
     * carries the wording they ask for, and `url` their homepage. A field of our own at the
     * document root would be legal, but invisible to every other STAC client.
     */
    attributedTo: string;
    /** Their homepage. Optional. */
    attributionUrl: string;
    /** The form of words they ask to be credited with. Optional. */
    attributionCredit: string;
    spatialExtent: SpatialExtentChoice;
    /** Carried, never edited — a narrower extent cannot yet be stated here. */
    bbox: number[];
    /** Whether the stored document already declared a narrower extent. */
    hasStoredRegionalExtent: boolean;
    temporalStart: string;
    temporalEnd: TemporalEndChoice;
    temporalEndDate: string;
    producedByNatureMetrics: boolean;
};

const creditedProvider = (values: CollectionFormValues): StacProvider => ({
    name: values.attributedTo.trim(),
    roles: ['licensor'],
    // Omitted rather than empty: a blank homepage is not a homepage.
    ...(values.attributionUrl.trim() ? { url: values.attributionUrl.trim() } : {}),
    ...(values.attributionCredit.trim() ? { description: values.attributionCredit.trim() } : {}),
});

export type CollectionFormErrors = Partial<Record<keyof CollectionFormValues, string>>;

/**
 * The licences a dataset may declare.
 *
 * A fixed list rather than free text, because the STAC schema pins `license` to
 * `^[\w\-\.\+]+$` — no spaces — and that rule is invisible until the engine answers 422.
 * STAC's vocabulary is an SPDX identifier, `proprietary` for data we do not publish, or
 * `various` where parts differ.
 */
export const LICENCE_OPTIONS = [
    { value: 'proprietary', label: 'Proprietary — not publicly available' },
    { value: 'various', label: 'Various — parts under different licences' },
    { value: 'CC-BY-4.0', label: 'CC BY 4.0' },
    { value: 'CC-BY-SA-4.0', label: 'CC BY-SA 4.0' },
    { value: 'CC0-1.0', label: 'CC0 1.0 — public domain' },
    { value: 'ODbL-1.0', label: 'Open Database Licence 1.0' },
    { value: 'OGL-UK-3.0', label: 'Open Government Licence 3.0 (UK)' },
] as const;

/** The schema's own rule, restated so a bad value is caught before the engine sees it. */
const LICENCE_PATTERN = /^[\w\-.+]+$/;

export const WHOLE_WORLD_BBOX = [-180, -90, 180, 90];

const isWholeWorld = (bbox: number[]): boolean => bbox.length === 4 && bbox.every((value, index) => value === WHOLE_WORLD_BBOX[index]);

export const emptyCollectionForm = (): CollectionFormValues => ({
    id: '',
    title: '',
    description: '',
    license: LICENCE_OPTIONS[0].value,
    attributedTo: '',
    attributionUrl: '',
    attributionCredit: '',
    spatialExtent: 'global',
    bbox: [...WHOLE_WORLD_BBOX],
    hasStoredRegionalExtent: false,
    // Valid from now onwards: a dataset is established before anything goes into it, so it
    // covers data from the moment it exists. A historical one is a correction away.
    temporalStart: utcDayOf(new Date()),
    temporalEnd: 'ongoing',
    temporalEndDate: '',
    producedByNatureMetrics: false,
});

const isBlank = (value: string) => value.trim().length === 0;

export const validateCollectionForm = (values: CollectionFormValues): CollectionFormErrors => {
    const errors: CollectionFormErrors = {};

    if (isBlank(values.id)) {
        errors.id = 'An identifier is required.';
    } else if (/[/\s]/.test(values.id.trim())) {
        errors.id = 'An identifier cannot contain slashes or spaces.';
    }

    if (isBlank(values.description)) errors.description = 'A description is required.';

    if (isBlank(values.license)) {
        errors.license = 'A licence is required.';
    } else if (!LICENCE_PATTERN.test(values.license.trim())) {
        errors.license = 'A licence must be an SPDX identifier — no spaces.';
    }

    // Stating a narrower extent needs a way to draw one. A dataset that already declares one
    // is still editable, or its description could never be corrected without widening it.
    if (values.spatialExtent === 'regional' && !values.hasStoredRegionalExtent) {
        errors.spatialExtent = 'Regional extents are not supported yet — a dataset must cover the globe.';
    }

    if (isBlank(values.attributedTo) && (!isBlank(values.attributionUrl) || !isBlank(values.attributionCredit))) {
        errors.attributedTo = 'A credit needs the name of who is being credited.';
    }

    if (isBlank(values.temporalStart)) {
        errors.temporalStart = 'A start date is required.';
    }

    if (values.temporalEnd === 'ended') {
        if (isBlank(values.temporalEndDate)) {
            errors.temporalEndDate = 'An end date is required, or the dataset is ongoing.';
        } else if (!isBlank(values.temporalStart) && moment.utc(values.temporalEndDate).isBefore(moment.utc(values.temporalStart))) {
            errors.temporalEndDate = 'The end date cannot be before the start date.';
        }
    }

    return errors;
};

/**
 * `existing` is the document the engine returned. Its fields are the base, so an edit
 * changes what the form owns and leaves everything else exactly as stored — `links` and
 * `keywords` included, neither of which the form shows.
 */
export const toCollectionDocument = (values: CollectionFormValues, existing?: StacCollection): StacCollection => {
    const title = values.title.trim();

    const document: StacCollection = {
        ...(existing ?? {}),
        id: values.id.trim(),
        type: 'Collection',
        stac_version: existing?.stac_version ?? '1.0.0',
        description: values.description.trim(),
        license: values.license.trim(),
        extent: {
            spatial: {
                bbox: [values.spatialExtent === 'global' ? [...WHOLE_WORLD_BBOX] : [...values.bbox]],
            },
            temporal: {
                // An open end is `null` — the dataset is still being added to. A closed one
                // ends at the close of the chosen day, so that day is inside the interval.
                interval: [
                    [
                        moment.utc(values.temporalStart, 'YYYY-MM-DD').startOf('day').toISOString(),
                        values.temporalEnd === 'ongoing'
                            ? null
                            : moment.utc(values.temporalEndDate, 'YYYY-MM-DD').endOf('day').toISOString(),
                    ],
                ],
            },
        },
        links: existing?.links ?? [],
    };

    if (title) {
        document.title = title;
    } else {
        delete document.title;
    }

    // Only the two entries this form owns are its to add or remove. A host or a processor the
    // document already names is none of its business and stays exactly as it was.
    const others = (existing?.providers ?? []).filter(provider => !isOurProvider(provider) && !isCreditedProvider(provider));
    const providers = [
        ...others,
        ...(values.producedByNatureMetrics ? [NATUREMETRICS_PROVIDER] : []),
        ...(values.attributedTo.trim() ? [creditedProvider(values)] : []),
    ];

    if (providers.length > 0) {
        document.providers = providers;
    } else {
        delete document.providers;
    }

    return document;
};

const dayOf = (instant?: string | null): string => (instant ? moment.utc(instant).format('YYYY-MM-DD') : '');

export const formFromCollection = (collection: StacCollection): CollectionFormValues => {
    const bbox = collection.extent?.spatial?.bbox?.[0] ?? [...WHOLE_WORLD_BBOX];
    const [start, end] = collection.extent?.temporal?.interval?.[0] ?? [null, null];
    const isRegional = !isWholeWorld(bbox);
    const credited = (collection.providers ?? []).find(isCreditedProvider);

    return {
        id: collection.id,
        title: collection.title ?? '',
        description: collection.description,
        license: collection.license,
        spatialExtent: isRegional ? 'regional' : 'global',
        bbox: [...bbox],
        hasStoredRegionalExtent: isRegional,
        temporalStart: dayOf(start),
        temporalEnd: end ? 'ended' : 'ongoing',
        temporalEndDate: dayOf(end),
        attributedTo: credited?.name ?? '',
        attributionUrl: credited?.url ?? '',
        attributionCredit: credited?.description ?? '',
        producedByNatureMetrics: (collection.providers ?? []).some(isOurProvider),
    };
};
